/**
 * Typed calls of the rider's signed "Saldo Vello" (D737a, ADR-0150; the frozen contract of doc 32 §3).
 *
 *  - `RiderWalletApi` (RIDER session): `GET /v1/rider/wallet?market=` — the session rider's balance (credit − open debt) and movements.
 *  - `RiderWalletAdminApi` (operator session, market-scoped GEO grant): a rider's wallet + pending adjustments (`rider_wallet:view`),
 *    propose a credit/debit (`rider_wallet:propose`), decide one (`rider_wallet:approve`, never the maker) and the approvals inbox.
 *
 * Money crosses the wire as base-10 strings of minor units with the ISO 4217 currency; the decoders turn them into `bigint` (never a
 * float) and refuse a body that is not the contract (`MALFORMED_RESPONSE`). The server takes every actor from the session — there is
 * no field here to name one.
 */
import { err, ok } from '@vello/core';
import { transportError } from './errors.js';
/** The reasons a direction admits (the server refuses any other: `RIDER_WALLET_REASON_INVALID`). */
export const RIDER_WALLET_REASONS_BY_DIRECTION = {
    CREDIT: ['GOODWILL', 'SERVICE_RECOVERY', 'CORRECTION', 'CASH_TOP_UP_AT_DESK'],
    DEBIT: ['CORRECTION'],
};
// ---- decoders -------------------------------------------------------------------------------------------------------------
const ENTRY_KINDS = ['ADJUSTMENT_CREDIT', 'ADJUSTMENT_DEBIT', 'DEBT_ACCRUED', 'DEBT_SETTLED', 'CREDIT_USED'];
const REASONS = ['GOODWILL', 'SERVICE_RECOVERY', 'CORRECTION', 'CASH_TOP_UP_AT_DESK'];
const STATUSES = ['PENDING_APPROVAL', 'APPLIED', 'REJECTED'];
const SIGNED = /^-?\d{1,19}$/;
const UNSIGNED = /^\d{1,19}$/;
const ISO4217 = /^[A-Z]{3}$/;
class Malformed extends Error {
}
const rec = (v, what) => {
    if (typeof v !== 'object' || v === null || Array.isArray(v))
        throw new Malformed(`${what} is not an object`);
    return v;
};
const text = (o, k) => {
    const v = o[k];
    if (typeof v !== 'string')
        throw new Malformed(`${k} is not a string`);
    return v;
};
const textOrNull = (o, k) => (o[k] === null || o[k] === undefined ? null : text(o, k));
const minor = (o, k, signed) => {
    const v = text(o, k);
    if (!(signed ? SIGNED : UNSIGNED).test(v))
        throw new Malformed(`${k} is not an amount of minor units`);
    return BigInt(v);
};
const oneOf = (o, k, values) => {
    const v = text(o, k);
    if (!values.includes(v))
        throw new Malformed(`${k} is not a known value`);
    return v;
};
const decoded = (r, decode) => {
    if (!r.ok)
        return r;
    try {
        return ok(decode(r.value));
    }
    catch (e) {
        if (e instanceof Malformed)
            return err(transportError('MALFORMED_RESPONSE', e.message));
        throw e;
    }
};
function entryOf(v) {
    const o = rec(v, 'entry');
    const rideId = textOrNull(o, 'rideId');
    return { id: text(o, 'id'), kind: oneOf(o, 'kind', ENTRY_KINDS), amountMinor: minor(o, 'amountMinor', true), reasonCode: text(o, 'reasonCode'), at: text(o, 'at'), ...(rideId !== null ? { rideId } : {}) };
}
/** Decode the rider's wallet (`GET /v1/rider/wallet`). Throws on a body that is not the contract; the API classes answer it `MALFORMED_RESPONSE`. */
export function decodeRiderWallet(v) {
    const o = rec(v, 'wallet');
    const currency = text(o, 'currency');
    if (!ISO4217.test(currency))
        throw new Malformed('currency is not ISO 4217');
    const entries = o.entries;
    if (!Array.isArray(entries))
        throw new Malformed('entries is not a list');
    const view = {
        market: text(o, 'market'), currency, balanceMinor: minor(o, 'balanceMinor', true), creditMinor: minor(o, 'creditMinor', false), debtMinor: minor(o, 'debtMinor', false), entries: entries.map(entryOf),
    };
    if (view.balanceMinor !== view.creditMinor - view.debtMinor)
        throw new Malformed('balanceMinor is not creditMinor − debtMinor');
    return view;
}
export function decodeRiderWalletAdjustment(v) {
    const o = rec(v, 'adjustment');
    const routing = oneOf(o, 'routing', ['SINGLE', 'FOUR_EYES']);
    return {
        adjustmentId: text(o, 'adjustmentId'), riderId: text(o, 'riderId'), marketId: text(o, 'marketId'), direction: oneOf(o, 'direction', ['CREDIT', 'DEBIT']),
        amountMinor: minor(o, 'amountMinor', false), currency: text(o, 'currency'), reasonCode: oneOf(o, 'reasonCode', REASONS), note: text(o, 'note'),
        status: oneOf(o, 'status', STATUSES), routing, routeReason: text(o, 'routeReason'), policyVersion: text(o, 'policyVersion'),
        thresholdPolicy: textOrNull(o, 'thresholdPolicy'), thresholdMinor: o.thresholdMinor === null || o.thresholdMinor === undefined ? null : minor(o, 'thresholdMinor', false),
        proposedBy: text(o, 'proposedBy'), proposedAt: text(o, 'proposedAt'), decidedBy: textOrNull(o, 'decidedBy'), decidedAt: textOrNull(o, 'decidedAt'),
        decisionReason: textOrNull(o, 'decisionReason'), ledgerTransactionId: textOrNull(o, 'ledgerTransactionId'),
    };
}
const list = (v, k, each) => {
    const xs = rec(v, 'body')[k];
    if (!Array.isArray(xs))
        throw new Malformed(`${k} is not a list`);
    return xs.map(each);
};
// ---- the rider -------------------------------------------------------------------------------------------------------------
export class RiderWalletApi {
    client;
    constructor(client) {
        this.client = client;
    }
    /** The session rider's signed balance and latest movements in a market. */
    async get(marketId) {
        return decoded(await this.client.request({ method: 'GET', path: `/v1/rider/wallet?market=${encodeURIComponent(marketId)}`, auth: true }), decodeRiderWallet);
    }
}
// ---- the operator ----------------------------------------------------------------------------------------------------------
export class RiderWalletAdminApi {
    client;
    constructor(client) {
        this.client = client;
    }
    /** A rider's balance, movements and pending adjustments in a market (`rider_wallet:view`). */
    async get(riderId, marketId) {
        const r = await this.client.request({ method: 'GET', path: `/v1/ops/riders/${encodeURIComponent(riderId)}/wallet?market=${encodeURIComponent(marketId)}`, auth: true });
        return decoded(r, (v) => ({ ...decodeRiderWallet(v), riderId: text(rec(v, 'wallet'), 'riderId'), pending: list(v, 'pending', decodeRiderWalletAdjustment) }));
    }
    /**
     * Propose a credit or debit (the maker). At/below the governed threshold and within the maker's grant it is APPLIED at once (`SINGLE`);
     * otherwise it waits for a different operator (`FOUR_EYES`). Refusals: `RIDER_WALLET_AMOUNT_INVALID`, `RIDER_WALLET_REASON_INVALID`,
     * `RIDER_WALLET_NOTE_REQUIRED`, `RIDER_CREDIT_INSUFFICIENT`, `SELF_ADJUSTMENT`.
     */
    async propose(riderId, input) {
        const r = await this.client.request({
            method: 'POST', path: `/v1/ops/riders/${encodeURIComponent(riderId)}/wallet/adjustments`, auth: true,
            body: { marketId: input.marketId, direction: input.direction, amountMinor: input.amountMinor.toString(), reasonCode: input.reasonCode, note: input.note },
        });
        return decoded(r, (v) => ({ adjustment: decodeRiderWalletAdjustment(rec(v, 'body').adjustment) }));
    }
    /** The checker (never the maker, never the rider) approves or rejects (a reason is required to reject); a debit is re-checked against the credit. */
    async decide(adjustmentId, input) {
        const r = await this.client.request({ method: 'POST', path: `/v1/ops/rider-wallet/adjustments/${encodeURIComponent(adjustmentId)}/decision`, auth: true, body: input });
        return decoded(r, (v) => ({ adjustment: decodeRiderWalletAdjustment(rec(v, 'body').adjustment) }));
    }
    /** The approvals inbox of a market (`rider_wallet:approve`); PENDING_APPROVAL by default. */
    async inbox(marketId, status = 'PENDING_APPROVAL') {
        const r = await this.client.request({ method: 'GET', path: `/v1/ops/rider-wallet/adjustments?market=${encodeURIComponent(marketId)}&status=${status}`, auth: true });
        return decoded(r, (v) => ({ market: text(rec(v, 'body'), 'market'), status: oneOf(rec(v, 'body'), 'status', STATUSES), adjustments: list(v, 'adjustments', decodeRiderWalletAdjustment) }));
    }
}
//# sourceMappingURL=rider-wallet.js.map